import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Loader2, RefreshCw, Send, CheckCircle2, AlertCircle, Clock } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { listLeadsAdmin, retrySheetAppendAdmin } from "@/lib/leads.functions";

const STORAGE_KEY = "znith_admin_password";

type Lead = {
  id: string;
  created_at: string;
  nome: string;
  email: string;
  telefone: string;
  empresa: string;
  modalidade: string;
  urgencia: string;
  status: string;
  sheet_status: string;
  sheet_error: string | null;
  sheet_sent_at: string | null;
};

export const Route = createFileRoute("/admin/leads")({
  head: () => ({
    meta: [
      { title: "Admin — Leads | ZNITH" },
      { name: "robots", content: "noindex, nofollow" },
      { name: "description", content: "Painel interno de leads do SPRINT CODE." },
      { property: "og:title", content: "Admin — Leads | ZNITH" },
      { property: "og:description", content: "Painel interno de leads do SPRINT CODE." },
    ],
  }),
  component: AdminLeads,
});

function AdminLeads() {
  const list = useServerFn(listLeadsAdmin);
  const retry = useServerFn(retrySheetAppendAdmin);

  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(false);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<"todos" | "sucesso" | "erro">("todos");
  const [retrying, setRetrying] = useState<string | null>(null);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      setPassword(saved);
      void load(saved, "todos");
    }
  }, []);

  async function load(pwd: string, f: "todos" | "sucesso" | "erro") {
    setLoading(true);
    try {
      const res = await list({ data: { password: pwd, filter: f, limit: 200 } });
      if (!res.ok) {
        toast.error(res.error);
        if (res.error.includes("Senha")) {
          localStorage.removeItem(STORAGE_KEY);
          setAuthed(false);
        }
        return;
      }
      setLeads(res.leads as Lead[]);
      setAuthed(true);
      localStorage.setItem(STORAGE_KEY, pwd);
    } catch (e) {
      console.error(e);
      toast.error("Erro ao carregar leads");
    } finally {
      setLoading(false);
    }
  }

  async function handleRetry(leadId: string) {
    setRetrying(leadId);
    try {
      const res = await retry({ data: { password, leadId } });
      if (res.ok) {
        toast.success("Linha enviada para a planilha!");
        await load(password, filter);
      } else {
        toast.error(`Falhou: ${res.error}`);
      }
    } finally {
      setRetrying(null);
    }
  }

  if (!authed) {
    return (
      <main className="min-h-screen bg-midnight text-foreground">
        <div className="container-narrow flex min-h-screen items-center justify-center">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void load(password, filter);
            }}
            className="w-full max-w-sm space-y-4 rounded-2xl border border-border bg-card/40 p-8"
          >
            <h1 className="text-xl font-semibold">Admin — acesso restrito</h1>
            <div className="space-y-2">
              <Label>Senha</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
              />
            </div>
            <Button type="submit" disabled={loading || !password} className="w-full">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Entrar"}
            </Button>
          </form>
        </div>
      </main>
    );
  }

  const counts = {
    total: leads.length,
    sucesso: leads.filter((l) => l.sheet_status === "enviado").length,
    erro: leads.filter((l) => l.sheet_status === "erro").length,
    pendente: leads.filter((l) => l.sheet_status === "pendente").length,
  };

  return (
    <main className="min-h-screen bg-midnight text-foreground">
      <div className="container-narrow py-10">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">Leads — log de envios</h1>
            <p className="mt-1 text-sm text-slate-muted">
              {counts.total} leads · {counts.sucesso} na planilha · {counts.erro} com erro · {counts.pendente} pendentes
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select
              value={filter}
              onValueChange={(v) => {
                const f = v as "todos" | "sucesso" | "erro";
                setFilter(f);
                void load(password, f);
              }}
            >
              <SelectTrigger className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                <SelectItem value="sucesso">Só sucesso</SelectItem>
                <SelectItem value="erro">Só com erro</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              onClick={() => load(password, filter)}
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              Atualizar
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                localStorage.removeItem(STORAGE_KEY);
                setAuthed(false);
                setPassword("");
              }}
            >
              Sair
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto rounded-2xl border border-border bg-card/40">
          <table className="w-full text-sm">
            <thead className="bg-charcoal/60 text-xs uppercase tracking-wider text-slate-muted">
              <tr>
                <th className="px-4 py-3 text-left">Data</th>
                <th className="px-4 py-3 text-left">Nome</th>
                <th className="px-4 py-3 text-left">E-mail</th>
                <th className="px-4 py-3 text-left">Empresa</th>
                <th className="px-4 py-3 text-left">Modalidade</th>
                <th className="px-4 py-3 text-left">Banco</th>
                <th className="px-4 py-3 text-left">Planilha</th>
                <th className="px-4 py-3 text-left">Ação</th>
              </tr>
            </thead>
            <tbody>
              {leads.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-slate-muted">
                    Nenhum lead encontrado.
                  </td>
                </tr>
              )}
              {leads.map((l) => (
                <tr key={l.id} className="border-t border-border/40">
                  <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-muted">
                    {new Date(l.created_at).toLocaleString("pt-BR", {
                      timeZone: "America/Sao_Paulo",
                    })}
                  </td>
                  <td className="px-4 py-3">{l.nome}</td>
                  <td className="px-4 py-3 text-xs">{l.email}</td>
                  <td className="px-4 py-3">{l.empresa}</td>
                  <td className="px-4 py-3 text-xs">{l.modalidade}</td>
                  <td className="px-4 py-3">
                    <Badge tone="success">
                      <CheckCircle2 className="h-3 w-3" /> Salvo
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    {l.sheet_status === "enviado" && (
                      <Badge tone="success">
                        <CheckCircle2 className="h-3 w-3" /> Enviado
                      </Badge>
                    )}
                    {l.sheet_status === "erro" && (
                      <div className="space-y-1">
                        <Badge tone="error">
                          <AlertCircle className="h-3 w-3" /> Erro
                        </Badge>
                        {l.sheet_error && (
                          <p className="max-w-xs truncate text-[10px] text-destructive" title={l.sheet_error}>
                            {l.sheet_error}
                          </p>
                        )}
                      </div>
                    )}
                    {l.sheet_status === "pendente" && (
                      <Badge tone="warn">
                        <Clock className="h-3 w-3" /> Pendente
                      </Badge>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {l.sheet_status !== "enviado" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRetry(l.id)}
                        disabled={retrying === l.id}
                      >
                        {retrying === l.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Send className="h-3 w-3" />
                        )}
                        Reenviar
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </main>
  );
}

function Badge({
  tone,
  children,
}: {
  tone: "success" | "error" | "warn";
  children: React.ReactNode;
}) {
  const cls =
    tone === "success"
      ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"
      : tone === "error"
        ? "bg-destructive/10 text-destructive border-destructive/30"
        : "bg-amber-500/10 text-amber-400 border-amber-500/30";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${cls}`}
    >
      {children}
    </span>
  );
}
